import React, { useState, useEffect } from 'react';
import { sanitizeCmsHtml } from '@agentic/magento-client/lib/cms-sanitize';
import { responsiveCmsImages } from '../../../lib/responsive-cms-images';

interface RichTextProps {
  data: {
    html?: string;
    content?: string;
    identifier?: string;
    align?: 'left' | 'center' | 'right';
    maxWidth?: string;
    config?: {
      cmsBlockIdentifier?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  styles?: string;
  context?: unknown;
}

export const RichText: React.FC<RichTextProps> = ({ data, styles, context }) => {
  const { align = 'left', maxWidth = 'none', config, identifier } = data ?? {};
  const initialHtml = data?.html || data?.content || '';
  // textMode: 'auto' (default, follows theme) | 'light' (white text) | 'dark' (dark text)
  // Set via preset block content or admin block settings. Scopes --cms-text
  // overrides onto the wrapper so all child elements (cms-blocks.css rules)
  // pick up the correct colour without hardcoding.
  const textModeRaw = (data as { textMode?: string } | undefined)?.textMode;
  const textMode: string = typeof textModeRaw === 'string' ? textModeRaw : 'auto';

  // Variant — controls how the CMS HTML is wrapped/laid out (per spec doc 11).
  // Defined values: flush | compact | card | hero-centered |
  //                 image-text-side | image-text-stacked |
  //                 icon-row | horizontal-strip
  // The variant only adds a `data-cms-variant` attribute on the wrapper;
  // CSS in _style-presets.css handles each pattern using --style-* and
  // --color-* tokens only (no hardcoded values).
  const variantRaw = (data as { variant?: string } | undefined)?.variant
    ?? (data as { content?: { variant?: string } } | undefined)?.content?.variant
    ?? (data as { settings?: { variant?: string } } | undefined)?.settings?.variant
    ?? '';
  const variant: string = typeof variantRaw === 'string' ? variantRaw : '';

  // List layout — set in the editor (BlockContentPanel). Maps a <ul>/<li> list
  // inside the CMS content to horizontal / horizontal-scroll / vertical flow via
  // `data-list-layout` on .cms-host. CSS lives in cms/_list-layout.css. Empty /
  // 'default' leaves the block's own class-hook styling untouched.
  const listLayoutRaw = (data as { listLayout?: string } | undefined)?.listLayout
    ?? (data as { content?: { listLayout?: string } } | undefined)?.content?.listLayout
    ?? (data as { settings?: { listLayout?: string } } | undefined)?.settings?.listLayout
    ?? '';
  const listLayout: string = typeof listLayoutRaw === 'string' && listLayoutRaw !== 'default' ? listLayoutRaw : '';

  // Card Frame — editor toggle (content.cardFrame). When on, the block adopts
  // the active style preset's card border + shadow + hover via the
  // `.cms-card-frame` class (CSS in cms/_hero-enhancement.css). Preset-adaptive
  // (not brutalist-specific). Settable per block in the editor AND in presets.
  const cardFrame: boolean =
    (data as { cardFrame?: boolean } | undefined)?.cardFrame
    ?? (data as { content?: { cardFrame?: boolean } } | undefined)?.content?.cardFrame
    ?? (data as { settings?: { cardFrame?: boolean } } | undefined)?.settings?.cardFrame
    ?? false;

  // Resolve the CMS HTML SYNCHRONOUSLY so it is present in the SERVER-RENDERED
  // markup. This resolution used to live only inside useEffect, which meant a
  // block referenced by identifier (every footer block) rendered `null` on the
  // server via the `if (!contentHtml) return null` guard below — its content,
  // and therefore every link inside it, existed in the delivered HTML only as
  // serialized hydration props. Crawlers saw no footer links at all (Ahrefs
  // flagged /contact-us and the blog category hubs as having zero inbound
  // links). `data` and `context` are serialized props, so this is deterministic
  // across SSR and the first client render — no React #418.
  const resolveCmsHtml = (): string => {
    // If we already have explicit HTML content, use it.
    if (initialHtml && !identifier && !config?.cmsBlockIdentifier) return initialHtml;

    const targetIdentifier = identifier || config?.cmsBlockIdentifier;
    if (!targetIdentifier) return initialHtml;

    // Check SSR-injected CMS content first (from LayoutRendererEnhanced pre-fetch)
    interface CmsCacheData { cmsContent?: string; _cmsCache?: Record<string, { content?: string } | undefined> }
    const cacheData = data as CmsCacheData | undefined;
    const ssrContent: string | undefined = cacheData?.cmsContent ?? cacheData?._cmsCache?.[targetIdentifier]?.content;
    if (ssrContent) return ssrContent;

    // Check context next
    interface CmsBlockEntry { identifier?: string; content?: string }
    const ctxBlocks = (context as { cmsBlocks?: CmsBlockEntry[] } | undefined)?.cmsBlocks;
    const block = ctxBlocks?.find((b: CmsBlockEntry) => b.identifier === targetIdentifier);
    if (block?.content) return block.content;

    // Not in the synced CMS data → leave content empty so the slot COLLAPSES
    // (.is-layout-container:empty → display:none, letting fill & centre recentre
    // the active block(s)). We deliberately do NOT fall back to a live Magento
    // cmsBlocks() fetch: prebuilt/SSR pages are built entirely from the CMS sync,
    // and the old fallback spammed Magento with per-block queries for identifiers
    // that don't exist (the "CMS block X doesn't exist" GraphQL error flood).
    return '';
  };

  const [contentHtml, setContentHtml] = useState<string>(resolveCmsHtml);

  // Re-resolve when the block's inputs change after mount (editor live preview).
  // resolveCmsHtml is intentionally not a dep: it closes over exactly the values
  // listed here and is redefined each render, so including it would re-run the
  // effect on every render.
  useEffect(() => {
    setContentHtml(resolveCmsHtml());
  }, [initialHtml, identifier, config, context, data]);

  if (!contentHtml) return null;

  // Apply footer-cms-block class when rendering CMS block content from an identifier.
  // The class originally styled *only* footer content (uppercase headings, compact
  // paragraphs, 40px image cap), but was applied here to ALL identifier-fetched
  // blocks. Audit pass 6 (2026-04-24) scoped every `.footer-cms-block` rule in
  // global.css to `footer .footer-cms-block`, so this wrapper is now a no-op
  // outside an actual <footer> element — hero / cat-scroll / htb / banner CMS
  // content is free to be styled by its semantic class hooks in cms-blocks.css.
  const isCmsBlock = !!(identifier || config?.cmsBlockIdentifier);
  // Add `cms-block-<identifier>` class so CSS can target specific blocks
  // by their Magento identifier (stable across saved-layout id regen).
  // Sanitize: lowercase, replace any non [a-z0-9_-] with `_`.
  const blockIdent = (identifier || config?.cmsBlockIdentifier || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');
  const identClass = blockIdent ? `cms-block-${blockIdent}` : '';
  const textModeClass = textMode !== 'auto' ? `text-mode-${textMode}` : '';
  const wrapperClass = [
    isCmsBlock ? 'footer-cms-block' : '',
    identClass,
    textModeClass,
    cardFrame ? 'cms-card-frame' : '',
  ].filter(Boolean).join(' ');

  // Defensive final pass — content from cms-cache and the client-side
  // fetch above is already sanitized, but this guards against any
  // future code path that bypasses both (e.g. directly setting
  // data.html in a layout JSON edited by hand).
  //
  // 2026-05-28 PR G5 G — preserve Page Builder structure when the
  // content carries `data-content-type=` markers. Verified live on
  // /gas-surveying-hire-rates: rich_text block rendered with
  // `dataContentTypeInRT: 0` and `dataPbStyleInRT: 0` — the sanitiser
  // was stripping the row / column wrappers that the merchant designed
  // in Magento Page Builder, leaving content flat. The `preserveFlag`
  // here auto-detects PB content; an explicit
  // `data.preservePageBuilderLayout: true/false` setting overrides the
  // detection (admin merchant gets the toggle in the editor — content
  // / structure preset layer).
  const explicitPreserve = (data as { preservePageBuilderLayout?: boolean } | undefined)?.preservePageBuilderLayout;
  const detectedPreserve = /data-content-type=/i.test(contentHtml);
  const preservePB = explicitPreserve != null ? !!explicitPreserve : detectedPreserve;
  let finalHtml = sanitizeCmsHtml(contentHtml, { preservePageBuilderLayout: preservePB });
  finalHtml = responsiveCmsImages(finalHtml, identifier || config?.cmsBlockIdentifier || '');

  // A11y safety net: certain seed CMS blocks (`why_choose_us_values`,
  // `call_the_experts`, etc.) ship with `<h4>` items that sit directly
  // under an H2 section heading. Promote them to `<h3>` so the document
  // outline stays valid (no skipped levels). Visual styling is governed
  // by the block's class hooks in cms-blocks.css, so this swap doesn't
  // affect appearance — only the accessibility tree.
  const targetIdentifier = identifier || config?.cmsBlockIdentifier;
  if (targetIdentifier && /^(why_choose_us_values|call_the_experts)$/.test(targetIdentifier)) {
    finalHtml = finalHtml
      .replace(/<h4(\s[^>]*)?>/gi, '<h3$1>')
      .replace(/<\/h4>/gi, '</h3>');
  }

  // PROSE GATE — `prose` (Tailwind Typography) injects bullet lists,
  // image margins, border-radius, and padding rules that mangle our
  // structured CMS markup.
  //
  // ARCHITECTURE DECISION:
  //   ALL CMS-block content (anything fetched by identifier) bypasses
  //   prose unconditionally. CMS blocks ship with semantic class hooks
  //   (services-strip, accreditations, htb-item, banner--*, etc.) and
  //   are styled by cms-blocks.css + global.css `.footer-cms-block`.
  //   Prose is only useful for unstructured rich-text-editor output
  //   (e.g. authored body content with no class scaffolding) — passing
  //   it through CMS-block markup ALWAYS causes regressions:
  //     • prose ul → list-style:disc + padding-left → bullets on link
  //       columns and pill clouds
  //     • prose img → margin + border-radius → breaks tile layouts
  //     • prose p → font-size 1.125rem + margin → overrides eyebrow,
  //       caption, footer-cms-block compact text
  //
  // For literal/inline HTML (no identifier), keep the prose treatment —
  // that's the path used when a layout author drops raw rich text into
  // a layout JSON for, e.g., a blog post body.
  //
  // STRUCTURED_CLASSES is retained as a fallback safety net for the
  // literal-HTML path (which can also accidentally include semantic
  // markup like .htb-item if the author copies seed content).
  const STRUCTURED_CLASSES = [
    'cat-scroll', 'cat-brand-strip', 'cat-5up', 'cat-tiles', 'subcat-tiles',
    'promo-grid', 'promo-3up', 'promo-2up-large', 'blog-4up',
    'brand-carousel', 'brand-story-strip',
    'banner', 'banner--', 'promo-banner', 'promo-wide',
    'hero-bg', 'hero-image', 'hero-quicklinks',
    'category-hero', 'cat-banner-top', 'blog-hero', 'trade-hero',
    'season-story', 'product-story', 'editorial-image', 'editor-shot',
    'payment-editorial', 'auth-editorial',
    'htb', 'htb-item',
    'popular-terms', 'seo-tabs',
    'why-values', 'value-props', 'value-prop',
    'cat-sidebar-promo',
    'eyebrow', 'caption',
    'accreditations', 'payment-methods', 'services-strip', 'social-links',
    'legal-links', 'footer-links',
  ];
  const hasStructured = STRUCTURED_CLASSES.some(cls =>
    finalHtml.includes(`class="${cls}`) ||
    finalHtml.includes(`class="${cls} `) ||
    finalHtml.includes(` ${cls}`) ||
    finalHtml.includes(`"${cls}"`)
  );

  // CMS-block path → never prose. Literal HTML path → prose unless
  // structured markup is detected.
  //
  // PR G8 — CMS PAGE body. LayoutRendererEnhanced sets data.cmsPage =
  // true when it injected a CMS page's content into this block. Pages
  // authored in Magento Page Builder export with deeply-nested classless
  // divs (verified on /gas-surveying-hire-rates — no `data-content-type`,
  // no `pagebuilder-*` classes). Prose treats the whole thing as one
  // long article and stacks everything vertically. The `cms-page-body`
  // wrapper here switches in pattern-recognition CSS that detects
  // sibling card structures (img+text+CTA) and lays them out as cards.
  const isCmsPageBody = !!(data as { cmsPage?: boolean } | undefined)?.cmsPage;
  const proseClasses = isCmsPageBody
    ? 'cms-host cms-page-body'
    : (isCmsBlock || hasStructured)
      ? 'cms-host'
      : 'prose prose-lg max-w-none text-[var(--text-secondary)] prose-headings:text-[var(--text-primary)] prose-headings:font-semibold prose-a:text-[var(--color-accent-fg)] prose-strong:text-[var(--text-primary)] prose-li:marker:text-[var(--color-accent-fg)] leading-relaxed';

  return (
    <div className={`w-full ${styles ?? ''}`}>
      <div
        className={`w-full ${proseClasses} ${wrapperClass}`}
        data-cms-variant={variant || undefined}
        data-list-layout={listLayout || undefined}
        style={{ textAlign: align, maxWidth }}
        dangerouslySetInnerHTML={{ __html: finalHtml }}
      />
    </div>
  );
};
